import { gql, useQuery } from '@apollo/client';
import Input from '@orn/shared-web/components/Input';
import { DollarIcon } from '@orn/shared-web/icons';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { useUpdateFamilySettings } from '../../../hooks/mutations/useUpdateFamilySettings';
import { useUpdateFinancialAccountSettings } from '../../../hooks/mutations/useUpdateFinancialAccountSettings';
import useAutoCommit from '../../../hooks/useAutoCommit';
import UpdateAccountModal from '../../UpdateAccountModal';
import { SettingsCard, SettingsTabLoading } from '../common';

import { buildAccountSettingsInput, partitionAccountAlerts } from './accountAlerts';
import AccountAlertsSection from './AccountAlertsSection';
import { formatDollars, formatPercent, validateDollars, validatePercent } from './notificationSettingsFields';

import type {
  GetNotificationSettingsQuery,
  GetNotificationSettingsQueryVariables,
} from './__generated__/NotificationSettingsForm.gql';
import type { IAvailableAccountAlert, IConfiguredAccountAlert } from './accountAlerts';

// ---------------------------------------------------------------------------
// Base (presentational) — renders the UI from props + callbacks.
// ---------------------------------------------------------------------------

export interface INotificationSettingsFormBaseProps {
  /** Low-balance threshold input value (dollars). */
  lowBalanceThreshold: string;
  onLowBalanceThresholdChange: (value: string) => void;
  onLowBalanceThresholdBlur: () => void;
  lowBalanceError?: string;
  /** Credit-utilization threshold input value (whole-number percent). */
  creditUtilizationThreshold: string;
  onCreditUtilizationThresholdChange: (value: string) => void;
  onCreditUtilizationThresholdBlur: () => void;
  creditUtilizationError?: string;
  saveError?: string;
  /** Eligible accounts that already override the household defaults. */
  configuredAccounts: IConfiguredAccountAlert[];
  /** Eligible accounts still inheriting the household defaults (the "add" picker options). */
  availableAccounts: IAvailableAccountAlert[];
  onEditAccount: (account: { id: string; name: string }) => void;
  onRemoveAccount: (account: IConfiguredAccountAlert) => void;
  removingAccountIds?: ReadonlySet<string>;
}

interface ISettingsFieldProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

const SettingsField = ({ title, description, children }: ISettingsFieldProps) => (
  <div className="flex flex-col gap-2">
    <div className="flex flex-col gap-0.5">
      <div className="text-primary text-sm font-medium">{title}</div>
      <div className="text-tertiary text-sm">{description}</div>
    </div>
    {children}
  </div>
);

/**
 * Presentational "Notification Preferences" settings form — the family-wide
 * low-balance ($) and credit-utilization (%) alert thresholds. Each field
 * persists on blur (no save button); fully controlled — the
 * `NotificationSettingsForm` wrapper (below) owns the query, state, and the
 * save mutation.
 */
export const NotificationSettingsFormBase = ({
  lowBalanceThreshold,
  onLowBalanceThresholdChange,
  onLowBalanceThresholdBlur,
  lowBalanceError,
  creditUtilizationThreshold,
  onCreditUtilizationThresholdChange,
  onCreditUtilizationThresholdBlur,
  creditUtilizationError,
  saveError,
  configuredAccounts,
  availableAccounts,
  onEditAccount,
  onRemoveAccount,
  removingAccountIds,
}: INotificationSettingsFormBaseProps) => (
  <div className="flex max-w-[736px] flex-col gap-5">
    <SettingsCard title="Notification Preferences" subtitle="Control notifications from Mantle.">
      <SettingsField
        title="Low account balance"
        description="Get notified when any account balance falls below this amount."
      >
        <Input
          type="number"
          placeholder="500.00"
          icon={DollarIcon}
          value={lowBalanceThreshold}
          onChange={onLowBalanceThresholdChange}
          onBlur={onLowBalanceThresholdBlur}
          isInvalid={Boolean(lowBalanceError)}
          hint={lowBalanceError || undefined}
          size="md"
          className="max-w-sm"
        />
      </SettingsField>

      <SettingsField
        title="High credit card utilization"
        description="Get notified when a credit card's balance reaches this percent of its limit."
      >
        <div className="flex max-w-sm items-start gap-2">
          <Input
            type="number"
            placeholder="90"
            value={creditUtilizationThreshold}
            onChange={onCreditUtilizationThresholdChange}
            onBlur={onCreditUtilizationThresholdBlur}
            isInvalid={Boolean(creditUtilizationError)}
            hint={creditUtilizationError || undefined}
            size="md"
            className="flex-1"
          />
          <span className="text-tertiary text-md shrink-0 pt-2.5">%</span>
        </div>
      </SettingsField>

      <AccountAlertsSection
        configuredAccounts={configuredAccounts}
        availableAccounts={availableAccounts}
        onEditAccount={onEditAccount}
        onRemoveAccount={onRemoveAccount}
        removingAccountIds={removingAccountIds}
      />
    </SettingsCard>

    {saveError && <span className="text-error-primary text-sm sm:self-end">{saveError}</span>}
  </div>
);

NotificationSettingsFormBase.displayName = 'NotificationSettingsFormBase';

// ---------------------------------------------------------------------------
// Wrapper (data) — fetches the thresholds + sets up the mutation.
// ---------------------------------------------------------------------------

const GET_NOTIFICATION_SETTINGS = gql`
  query GetNotificationSettings {
    getUser {
      id
      family {
        id
        settings {
          lowBalanceThreshold
          creditUtilizationThreshold
        }
      }
    }
    getFinancialAccountsForUser {
      id
      name
      type
      subtype
      mask
      settings {
        lowBalanceThreshold
        creditUtilizationThreshold
        disableThreshold
      }
    }
  }
`;

/**
 * "Notifications" settings form wrapper. Fetches the family's low-balance and
 * credit-utilization thresholds, owns the input state, and persists each
 * valid, changed threshold via `updateFamilySettings` on blur — independently,
 * so an invalid value in one field never blocks the other. Renders
 * `NotificationSettingsFormBase` for UI.
 */
const NotificationSettingsForm = () => {
  const { data, loading: queryLoading } = useQuery<GetNotificationSettingsQuery, GetNotificationSettingsQueryVariables>(
    GET_NOTIFICATION_SETTINGS
  );
  const family = data?.getUser?.family;
  const settings = family?.settings;

  const [updateFamilySettings] = useUpdateFamilySettings();
  const [updateFinancialAccountSettings] = useUpdateFinancialAccountSettings();

  const [lowBalanceThreshold, setLowBalanceThreshold] = React.useState('');
  const [creditUtilizationThreshold, setCreditUtilizationThreshold] = React.useState('');
  const [touched, setTouched] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [editingAccount, setEditingAccount] = React.useState<{ id: string; name: string } | null>(null);
  const [removingAccountIds, setRemovingAccountIds] = React.useState<ReadonlySet<string>>(() => new Set());
  const hasHydratedRef = React.useRef(false);

  // Configured vs inheriting is derived fresh from the normalized cache every
  // render — the settings modal's mutation keeps it current, so the list stays
  // in sync without any local row state to seed or reconcile.
  const { configured, available } = React.useMemo(
    () => partitionAccountAlerts(data?.getFinancialAccountsForUser ?? []),
    [data?.getFinancialAccountsForUser]
  );

  // Seed the inputs once — re-seeding on every cache change would clobber
  // whatever the user is typing while a blur-save is still in flight.
  React.useEffect(() => {
    if (settings && !hasHydratedRef.current) {
      hasHydratedRef.current = true;
      setLowBalanceThreshold(formatDollars(settings.lowBalanceThreshold));
      setCreditUtilizationThreshold(formatPercent(settings.creditUtilizationThreshold));
    }
  }, [settings]);

  const lowBalanceError = touched ? validateDollars(lowBalanceThreshold) : undefined;
  const creditUtilizationError = touched ? validatePercent(creditUtilizationThreshold) : undefined;

  const handleLowBalanceChange = React.useCallback((value: string) => {
    setTouched(true);
    setLowBalanceThreshold(value);
  }, []);

  const handleCreditUtilizationChange = React.useCallback((value: string) => {
    setTouched(true);
    setCreditUtilizationThreshold(value);
  }, []);

  const lowBalanceNum = parseFloat(lowBalanceThreshold);
  const creditUtilizationNum = Number(creditUtilizationThreshold);

  // The thresholds are independent settings — each valid, changed field saves
  // on its own, so an invalid value in one never blocks the other.
  const commit = async () => {
    setSaveError(null);
    const input: { lowBalanceThreshold?: number; creditUtilizationThreshold?: number } = {};
    if (!validateDollars(lowBalanceThreshold) && lowBalanceNum !== settings?.lowBalanceThreshold) {
      input.lowBalanceThreshold = lowBalanceNum;
    }
    if (!validatePercent(creditUtilizationThreshold) && creditUtilizationNum !== settings?.creditUtilizationThreshold) {
      input.creditUtilizationThreshold = creditUtilizationNum;
    }
    if (Object.keys(input).length === 0) {
      return;
    }
    try {
      await updateFamilySettings({ variables: { input } });
    } catch (error) {
      logger.error('Failed to save notification settings', error);
      setSaveError("Couldn't save your changes. Please try again.");
    }
  };

  const requestCommit = useAutoCommit(commit);

  const handleLowBalanceBlur = React.useCallback(() => {
    const num = parseFloat(lowBalanceThreshold);
    if (!isNaN(num)) {
      setLowBalanceThreshold(formatDollars(num));
    }
    requestCommit();
  }, [lowBalanceThreshold, requestCommit]);

  // Clearing an override is a one-shot explicit action, so it writes directly
  // rather than through the family fields' blur-commit; a null threshold with
  // mute off reverts the account to the household default.
  const handleRemoveAccount = async (account: IConfiguredAccountAlert) => {
    setSaveError(null);
    setRemovingAccountIds((prev) => new Set(prev).add(account.id));
    try {
      await updateFinancialAccountSettings({
        variables: {
          input: buildAccountSettingsInput({ id: account.id, kind: account.kind, threshold: null, muted: false }),
        },
      });
    } catch (error) {
      logger.error('Failed to remove account alert override', error);
      setSaveError("Couldn't update the account's alerts. Please try again.");
    } finally {
      setRemovingAccountIds((prev) => {
        const next = new Set(prev);
        next.delete(account.id);
        return next;
      });
    }
  };

  if (queryLoading || !settings) {
    return <SettingsTabLoading />;
  }

  return (
    <>
      <NotificationSettingsFormBase
        lowBalanceThreshold={lowBalanceThreshold}
        onLowBalanceThresholdChange={handleLowBalanceChange}
        onLowBalanceThresholdBlur={handleLowBalanceBlur}
        lowBalanceError={lowBalanceError}
        creditUtilizationThreshold={creditUtilizationThreshold}
        onCreditUtilizationThresholdChange={handleCreditUtilizationChange}
        onCreditUtilizationThresholdBlur={requestCommit}
        creditUtilizationError={creditUtilizationError}
        saveError={saveError ?? undefined}
        configuredAccounts={configured}
        availableAccounts={available}
        onEditAccount={setEditingAccount}
        onRemoveAccount={handleRemoveAccount}
        removingAccountIds={removingAccountIds}
      />
      {editingAccount && (
        <UpdateAccountModal
          account={editingAccount}
          isOpen
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setEditingAccount(null);
            }
          }}
        />
      )}
    </>
  );
};

NotificationSettingsForm.displayName = 'NotificationSettingsForm';

export default NotificationSettingsForm;
